import React, { useState } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FiCheckSquare, FiBarChart2, FiZap ,FiAlertCircle } from "react-icons/fi";
import AvailabilityDp from "./Availabilitydp";
import CoolerImpurityDp from "./CoolerImpuritydp";
import ActivationDp from "./Activationdp";
import UnResolvedProductsDp from "./UnResolvedProductsDp";

function VarianceReport() {
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  };

  // Availability tab date state
  const [availFromDate, setAvailFromDate] = useState(getFirstDayOfMonth());
  const [availToDate,   setAvailToDate]   = useState(getLastDayOfMonth());

  // Cooler Purity tab date state
  const [soviFromDate,  setSoviFromDate]  = useState(getFirstDayOfMonth());
  const [soviToDate,    setSoviToDate]    = useState(getLastDayOfMonth());

  // Activation tab date state
  const [actFromDate,   setActFromDate]   = useState(getFirstDayOfMonth());
  const [actToDate,     setActToDate]     = useState(getLastDayOfMonth());

  const [unresolvedFromDate, setUnresolvedFromDate] = useState(getFirstDayOfMonth());
  const [unresolvedToDate,   setUnresolvedToDate]   = useState(getLastDayOfMonth());

  const handleAvailFromDate = (value) => {
    setAvailFromDate(value);
    if (value > availToDate) setAvailToDate(value);
  };
  const handleAvailToDate = (value) => {
    if (value < availFromDate) return;
    setAvailToDate(value);
  };

  const handleSoviFromDate = (value) => {
    setSoviFromDate(value);
    if (value > soviToDate) setSoviToDate(value);
  };
  const handleSoviToDate = (value) => {
    if (value < soviFromDate) return;
    setSoviToDate(value);
  };

  const handleActFromDate = (value) => {
    setActFromDate(value);
    if (value > actToDate) setActToDate(value);
  };
  const handleActToDate = (value) => {
    if (value < actFromDate) return;
    setActToDate(value);
  };

  const handleUnresolvedFromDate = (value) => { setUnresolvedFromDate(value); if (value > unresolvedToDate) setUnresolvedToDate(value); };
  const handleUnresolvedToDate   = (value) => { if (value < unresolvedFromDate) return; setUnresolvedToDate(value); };


  const datePickerSx = {
    width: "100%",
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid",
    borderColor: "gray.300",
    bg: "white",
    fontSize: "16px",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
      borderColor: "red.500",
      boxShadow: "0 0 0 3px rgba(244, 0, 9, 0.3)",
    },
  };

  const DateRangePicker = ({ fromDate, toDate, onFromChange, onToChange }) => (
    <Flex alignItems="center" gap={4}>
      <Box minW="220px">
        <Text fontSize="md" color="gray.800" fontWeight="bold" mb={1} mx={2}>
          From Date
        </Text>
        <Box
          as="input"
          type="date"
          value={fromDate}
          max={toDate}
          onChange={(e) => onFromChange(e.target.value)}
          sx={datePickerSx}
        />
      </Box>
      <Box minW="220px">
        <Text fontSize="md" fontWeight="bold" mb={1} color="gray.800" mx={2}>
          To Date
        </Text>
        <Box
          as="input"
          type="date"
          value={toDate}
          min={fromDate}
          onChange={(e) => onToChange(e.target.value)}
          sx={datePickerSx}
        />
      </Box>
    </Flex>
  );

  const tabStyle = {
    fontWeight: "semibold",
    fontSize: "md",
    py: 3,
    px: 6,
    borderRadius: "lg",
    color: "gray.600",
    display: "flex",
    gap: 2,
    alignItems: "center",
    _selected: { bg: "red.600", color: "white", boxShadow: "md" },
    _hover: { bg: "red.200", color: "red.700" },
  };

  return (
    <Box minH="100vh" bg="gray.50" pt={{ base: "10vh", md: "13vh" }} px={6}>
      {/* Page Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "flex-start", md: "center" }}
        mb={6}
      >
        <Box>
          <Heading size="lg" color="gray.800" mb={2}>
            Variance Reports
          </Heading>
          <Text color="gray.500">
            Monitor store availability, activation, and view SOVI &amp; RED score reports
          </Text>
        </Box>
      </Flex>

      {/* Main Tabs */}
      <Tabs variant="unstyled" isLazy>
        <TabList
          bg="red.100"
          p={2}
          borderRadius="lg"
          gap={2}
          display="inline-flex"
          mb={6}
        >
          <Tab {...tabStyle}>
            <FiCheckSquare size={18} /> Availability
          </Tab>
          <Tab {...tabStyle}>
            <FiZap size={18} /> Activation
          </Tab>
          <Tab {...tabStyle}>
            <FiBarChart2 size={18} /> Cooler Purity
          </Tab>
           <Tab {...tabStyle}><FiAlertCircle size={18} /> Unresolved Products</Tab>
        </TabList>

        <TabPanels>
          {/* ── Tab 1: Availability ── */}
          <TabPanel p={0}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              mb={6}
            >
              <Box>
                <Heading size="md" color="gray.700" mb={1}>
                  Availability
                </Heading>
                <Text color="gray.500" fontSize="sm">
                  Track store-level availability across date ranges
                </Text>
              </Box>
              <DateRangePicker
                fromDate={availFromDate}
                toDate={availToDate}
                onFromChange={handleAvailFromDate}
                onToChange={handleAvailToDate}
              />
            </Flex>
            <AvailabilityDp fromDate={availFromDate} toDate={availToDate} />
          </TabPanel>

          {/* ── Tab 2: Activation ── */}
          <TabPanel p={0}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              mb={6}
            >
              <Box>
                <Heading size="md" color="gray.700" mb={1}>
                  Activation
                </Heading>
                <Text color="gray.500" fontSize="sm">
                  View store-level activation scores and product qualification counts
                </Text>
              </Box>
              <DateRangePicker
                fromDate={actFromDate}
                toDate={actToDate}
                onFromChange={handleActFromDate}
                onToChange={handleActToDate}
              />
            </Flex>
            <ActivationDp fromDate={actFromDate} toDate={actToDate} />
          </TabPanel>

          {/* ── Tab 3: Cooler Purity ── */}
          <TabPanel p={0}>
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              mb={6}
            >
              <Box>
                <Heading size="md" color="gray.700" mb={1}>
                  SOVI &amp; RED Reports
                </Heading>
                <Text color="gray.500" fontSize="sm">
                  View RED scores and SOVI report by store
                </Text>
              </Box>
              <DateRangePicker
                fromDate={soviFromDate}
                toDate={soviToDate}
                onFromChange={handleSoviFromDate}
                onToChange={handleSoviToDate}
              />
            </Flex>
            <CoolerImpurityDp fromDate={soviFromDate} toDate={soviToDate} />
          </TabPanel>

          {/* 👇 Tab 4: Unresolved Products */}
          <TabPanel p={0}>
            <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} mb={6}>
              <Box>
                <Heading size="md" color="gray.700" mb={1}>Unresolved Products</Heading>
                <Text color="gray.500" fontSize="sm">Track products that remain unresolved across stores</Text>
              </Box>
              <DateRangePicker
                fromDate={unresolvedFromDate}
                toDate={unresolvedToDate}
                onFromChange={handleUnresolvedFromDate}
                onToChange={handleUnresolvedToDate}
              />
            </Flex>
            <UnResolvedProductsDp fromDate={unresolvedFromDate} toDate={unresolvedToDate} />
          </TabPanel>

        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default VarianceReport;